import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'sonner';

import {
  ApplyToProgramDocument,
  GetViewerAvailableProgramsDocument,
  GetViewerProgramEnrollmentsWithCoverageDocument,
} from '@sandtech-hub-monorepo/ehub-client';

import { trackProgramEnrolled } from '@sandtech-hub-monorepo/ui-auth-kit';

import { AppRoutesPaths } from '../routes/types';

interface UseEnrollInProgramResult {
  /** Enrol, then land the learner on their learning page. */
  enroll: (programId: string) => void;
  enrolling: boolean;
}

/**
 * Enrolling is the last step of picking how to start a program, not the first.
 * Both ways through the plan picker end here: "Stay on Free" enrols straight
 * away, and a paid plan enrols once the checkout is confirmed.
 *
 * Despite the mutation's name there is no admissions review on this path —
 * `applyToProgram` writes a ProgramEnrollment with status ONGOING and returns.
 * Enrolment is free and separate from the subscription, which is what unlocks
 * the paid modules of whatever it covers.
 *
 * Two call sites, so it stays a hook rather than being inlined.
 */
export const useEnrollInProgram = (): UseEnrollInProgramResult => {
  const navigate = useNavigate();

  /*
   * Read cache-first, for `isEnrolled` only. Every route into this already
   * queries the catalogue, so the answer is warm and costs no request.
   */
  const { data } = useQuery(GetViewerAvailableProgramsDocument, {
    fetchPolicy: 'cache-first',
  });

  /*
   * Both queries are refetched because both change: `availablePrograms` carries
   * the isEnrolled/canEnroll flags the catalogue and detail page render, and
   * the enrollment list is what the learning page lists. Awaited, so the
   * program is already there when the learner arrives rather than appearing a
   * moment later.
   */
  const [applyToProgram, { loading: enrolling }] = useMutation(
    ApplyToProgramDocument,
    {
      // The server's refusals name the actual problem, so they are shown
      // rather than replaced with a house message.
      onError: (error) => toast.error(error.message),
      onCompleted: (result, options) => {
        const programId = options?.variables?.programInput.programId;
        const program = result.applyToProgram?.user?.programEnrollments?.find(
          (pe) => pe.program?.id === programId
        )?.program;
        if (program) trackProgramEnrolled(program, result.applyToProgram?.user);
        toast.success('You are enrolled. Your free modules are open.');
        navigate(AppRoutesPaths.Learning);
      },
      refetchQueries: [
        GetViewerAvailableProgramsDocument,
        GetViewerProgramEnrollmentsWithCoverageDocument,
      ],
      awaitRefetchQueries: true,
    }
  );

  const enroll = (programId: string) => {
    /*
     * Already enrolled is not an error to show anyone. It is reachable
     * normally: the catalogue's Start button opens the picker whatever the
     * learner's state, and a second subscription bought for a program they are
     * already in comes back through here. `applyToProgram` would refuse it —
     * "Learner is already actively enrolled into this program" — so the
     * mutation is skipped and the learner just goes where they were going.
     */
    const alreadyEnrolled = (data?.viewer?.availablePrograms ?? []).some(
      (program) => program.id === programId && program.isEnrolled
    );

    if (alreadyEnrolled) {
      navigate(AppRoutesPaths.Learning);
      return;
    }

    applyToProgram({ variables: { programInput: { programId } } });
  };

  return { enroll, enrolling };
};
